/**
 * Step Form Display Utilities
 * Handles showing forms when steps are clicked
 */

import type { Dispatch } from '@reduxjs/toolkit';

import {
  MESSAGE_SUBTYPES,
  type STEP_KEYS,
  STEP_NAMES,
} from '../constants/steps';
import {
  addMessage,
  removeMessagesBySubType,
} from '../data-store/copilotSlice';

import {
  showAccountForm,
  showAddAccessForm,
  showConnectionForm,
  showGeneralForm,
  showRemoveAccessForm,
} from './stepFormUtils';

/**
 * Create instruction message for steps with instructions in edit mode
 */
const createInstructionMessage = (
  dispatch: Dispatch,
  stepId: string,
  instructions: string
): void => {
  const formattedInstructions = instructions.replace(/\\n/g, '\n').trim();

  const instructionMessage = {
    id: `instruction-${stepId}-${Date.now()}`,
    type: 'ai_response' as const,
    subType: MESSAGE_SUBTYPES.INSTRUCTION_PROMPT,
    text: `Hey, it seems you've already provided the additional information.\n\n${formattedInstructions}\n\nDo you want to continue with above instruction?`,
    timestamp: Date.now(),
    buttons: [
      { label: 'Yes', action: 'continue' },
      { label: 'No', action: 'account_not_aliging_with_previous_instruction' },
    ],
  };

  dispatch(addMessage(instructionMessage));
};

/**
 * Show form for a specific step
 */
const showStepForm = (
  dispatch: Dispatch,
  allStepFormData: Record<string, Record<string, unknown>>,
  stepId: string,
  isEditMode: boolean,
  getState?: () => unknown
): void => {
  if (isEditMode) {
    // Edit mode: Remove existing forms (only one form at a time)
    removeExistingForms(dispatch);
  }
  // Create mode: Don't remove existing forms (accumulate forms)

  // Get existing form data for pre-filling
  const existingFormData = allStepFormData[stepId] || {};

  // Show the appropriate form based on step
  switch (stepId) {
    case STEP_NAMES.GENERAL:
      showGeneralFormWithData(dispatch, existingFormData, getState);
      break;
    case STEP_NAMES.CONNECTION:
      showConnectionForm(dispatch, getState);
      break;
    case STEP_NAMES.CREATE_ACCOUNT:
      showAccountFormWithData(
        dispatch,
        'CREATE_ACCOUNT',
        STEP_NAMES.CREATE_ACCOUNT,
        existingFormData,
        isEditMode,
        getState
      );
      break;
    case STEP_NAMES.UPDATE_ACCOUNT:
      showAccountFormWithData(
        dispatch,
        'UPDATE_ACCOUNT',
        STEP_NAMES.UPDATE_ACCOUNT,
        existingFormData,
        isEditMode,
        getState
      );
      break;
    case STEP_NAMES.ENABLE_ACCOUNT:
      showAccountFormWithData(
        dispatch,
        'ENABLE_ACCOUNT',
        STEP_NAMES.ENABLE_ACCOUNT,
        existingFormData,
        isEditMode,
        getState
      );
      break;
    case STEP_NAMES.DISABLE_ACCOUNT:
      showAccountFormWithData(
        dispatch,
        'DISABLE_ACCOUNT',
        STEP_NAMES.DISABLE_ACCOUNT,
        existingFormData,
        isEditMode,
        getState
      );
      break;
    case STEP_NAMES.REMOVE_ACCOUNT:
      showAccountFormWithData(
        dispatch,
        'REMOVE_ACCOUNT',
        STEP_NAMES.REMOVE_ACCOUNT,
        existingFormData,
        isEditMode,
        getState
      );
      break;
    case STEP_NAMES.ADD_ACCESS:
      showAddAccessForm(dispatch, false);
      break;
    case STEP_NAMES.REMOVE_ACCESS:
      showRemoveAccessForm(dispatch, getState, false);
      break;
    default:
      // Other steps will be added later
      break;
  }

  // In edit mode, show instruction message AFTER the form
  if (
    isEditMode &&
    getState &&
    stepId !== 'Connection' &&
    stepId !== 'AddAccess' &&
    stepId !== 'RemoveAccess'
  ) {
    const state = getState() as {
      copilot: { parsedTemplates: Record<string, unknown> };
    };
    const parsedTemplates = state.copilot.parsedTemplates || {};
    const stepData = parsedTemplates[stepId] as
      | { instructions?: string }
      | undefined;

    if (stepData?.instructions && stepData.instructions.trim()) {
      createInstructionMessage(dispatch, stepId, stepData.instructions);
    }
  }
};

/**
 * Show General form with pre-filled data
 */
const showGeneralFormWithData = (
  dispatch: Dispatch,
  formData: Record<string, unknown>,
  getState?: () => unknown
): void => {
  showGeneralForm(dispatch, false, formData, getState);
};

/**
 * Show Account form with pre-filled data
 */
const showAccountFormWithData = (
  dispatch: Dispatch,
  stepKey: keyof typeof STEP_KEYS,
  stepName: string,
  formData: Record<string, unknown>,
  isInEditMode: boolean,
  getState?: () => unknown
): void => {
  showAccountForm(
    dispatch,
    stepKey,
    stepName,
    isInEditMode,
    formData,
    getState
  );
};

/**
 * Remove existing step forms from chat (for replacement)
 */
const removeExistingForms = (dispatch: Dispatch): void => {
  // Remove all possible step form subtypes
  const formSubtypes = [
    MESSAGE_SUBTYPES.BASIC_CONFIG,
    MESSAGE_SUBTYPES.CONNECTION_CONFIG,
    MESSAGE_SUBTYPES.CREATE_ACCOUNT_FORM,
    MESSAGE_SUBTYPES.UPDATE_ACCOUNT_FORM,
    MESSAGE_SUBTYPES.ENABLE_ACCOUNT_FORM,
    MESSAGE_SUBTYPES.DISABLE_ACCOUNT_FORM,
    MESSAGE_SUBTYPES.REMOVE_ACCOUNT_FORM,
    MESSAGE_SUBTYPES.ADD_ACCESS_FORM,
    MESSAGE_SUBTYPES.ACCESS_ATTRIBUTE_MAPPING,
    MESSAGE_SUBTYPES.ATTRIBUTE_MAPPING_START,
    MESSAGE_SUBTYPES.ATTRIBUTE_MAPPING_NEXT,
    MESSAGE_SUBTYPES.ADD_ACCESS_EDIT_PHASE,
    MESSAGE_SUBTYPES.USER_RESPONSE_NO,
    MESSAGE_SUBTYPES.USER_RESPONSE_YES,
    MESSAGE_SUBTYPES.REMOVE_ACCESS_FORM,
    MESSAGE_SUBTYPES.REMOVE_ACCESS_ATTRIBUTE_MAPPING,
    MESSAGE_SUBTYPES.INSTRUCTION_PROMPT,
    MESSAGE_SUBTYPES.ALL_ENTITLEMENT_DONE,
    MESSAGE_SUBTYPES.CONNECTION_STEP_ASSIST_INFO,
    MESSAGE_SUBTYPES.PROVIDE_ADDITIONAL_INFO,
    MESSAGE_SUBTYPES.FILE_DISPLAY,
    MESSAGE_SUBTYPES.MOVING_TO_NEXT_STEP,
    MESSAGE_SUBTYPES.CONFIRM_ONBOARD,
    MESSAGE_SUBTYPES.FINAL_CONFIRMATION,
    MESSAGE_SUBTYPES.SUCCESS,
    MESSAGE_SUBTYPES.ERROR,
    MESSAGE_SUBTYPES.SUCCESS,
    MESSAGE_SUBTYPES.THREE_BUTTONS_HELPER_TEXT,
    MESSAGE_SUBTYPES.ADD_ACCESS_UPDATE_SUCCESS,
    MESSAGE_SUBTYPES.REMOVE_ACCESS_UPDATE_SUCCESS,
    MESSAGE_SUBTYPES.REMOVE_ACCESS_SKIPPED,
    MESSAGE_SUBTYPES.FILE_EXTRACTION,
    MESSAGE_SUBTYPES.UPDATE_ACCOUNT_STEP_ASSIST_INFO,
    MESSAGE_SUBTYPES.SELECT_ONE_OF_OPTIONS,
  ];

  formSubtypes.forEach((subtype) => {
    dispatch(removeMessagesBySubType(subtype));
  });
};

export default showStepForm;
